<script setup lang="ts">
// Importar la interfaz Page y los componentes
import { Page } from "@/components/BreadCrumb.vue";
import * as BreadCrumbVue from "@/components/BreadCrumb.vue";
import useUser from "@/modules/users/composables/useUser";
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/vue";
import { PencilSquareIcon } from "@heroicons/vue/20/solid";

import { computed } from "vue";
import Tabs from "./partials/show";

import * as HasPermissionVue from "@/components/HasPermission.vue";
import useInternalUser from "../composables/useInternalUser";

// Registrar los componentes para usarlos en el template
const BreadCrumb = BreadCrumbVue;
const HasPermission = HasPermissionVue;

// Definir los componentes para el template
const components = {
  BreadCrumb,
  HasPermission
}

const { user } = useInternalUser({});

const pages = computed(
  () =>
    [
      {
        name: "Usuarios Internos",
        to: { name: "users.index.internal" },
        current: false,
      },
      {
        name: `Perfil de  ${user.value?.name}`,
        to: { name: "users.show.internal", params: { id: user.value?.id } },
      },
    ] as Page[],
);

// Para usuarios internos, siempre mostramos ambos tabs
const tabs = ["Información general", "Registro de cambios"];
const visibleTabs = Tabs;
</script>

<template>
  <div class="bg-slate-50 min-h-screen pb-10">
    <BreadCrumb :pages="pages" class="bg-white shadow-sm"></BreadCrumb>
    
    <!-- Header de perfil moderno -->
    <div class="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 pt-6">
      <div class="bg-white rounded-lg shadow-sm overflow-hidden">
        <div class="p-6 flex flex-col sm:flex-row items-center sm:items-center gap-6">
          <!-- Avatar del usuario -->
          <div class="flex-shrink-0 mx-auto sm:mx-0 flex justify-center w-full sm:w-auto">
            <img
              class="h-24 w-24 rounded-full ring-4 ring-indigo-50 bg-white object-cover shadow-sm"
              :src="
                user?.avatar ?
                  user.avatar
                : `https://ui-avatars.com/api/?name=${user?.name || 'Usuario'}&background=random&size=128`
              "
              alt="Foto de perfil"
            />
          </div>
          
          <!-- Información del usuario -->
          <div class="flex flex-col">
            <h1 class="text-2xl font-bold text-slate-900">
              {{ user?.name }} {{ user?.secondName }} {{ user?.lastname }}
              {{ user?.secondLastname }}
            </h1>
            <div class="flex flex-col gap-1" v-if="user?.email">
               <p class="text-sm text-slate-800">{{ user.email }}</p>
            </div>
            <div class="flex mt-2 flex-wrap gap-2" v-if="user?.roles && user.roles.length">
              <span v-for="role in user.roles" :key="role" class="inline-flex items-center rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-medium text-indigo-800">
                {{ role }}
              </span>
            </div>
          </div>
          
          <!-- Botones de acción -->
          <div class="mt-4 sm:mt-0 sm:ml-auto">
            <HasPermission
              :name-permission="'update-internal'"
              :entity-type="'App\\Models\\User'"
            >
              <RouterLink
                :to="{ name: 'users.edit.internal', params: { id: user?.id } }"
                class="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 transition-all hover:shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 items-center"
              >
                <PencilSquareIcon
                  class="-ml-1 mr-2 h-5 w-5"
                  aria-hidden="true"
                />
                <span>Editar información</span>
              </RouterLink>
            </HasPermission>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Contenido principal -->
    <div class="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 mt-6">
      
      <!-- Tarjeta principal con tabs -->
      <div class="bg-white rounded-lg shadow-sm overflow-hidden">
        <!-- Tabs -->
        <TabGroup>
          <div class="border-b border-gray-200">
            <TabList class="px-6">
              <nav
                class="flex space-x-8"
                aria-label="Tabs"
              >
                <Tab
                  v-for="tab in tabs"
                  :key="tab"
                  v-slot="{ selected }"
                  :aria-current="tab ? 'page' : undefined"
                >
                  <button
                    :class="[
                      selected ?
                        'border-indigo-500 text-gray-900'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                      'whitespace-nowrap border-b-2 py-4 px-1 text-md font-medium',
                    ]"
                  >
                    {{ tab }}
                  </button>
                </Tab>
              </nav>
            </TabList>
          </div>

          <TabPanels>
            <TabPanel
              v-for="(Element, index) in Tabs"
              :key="index"
            >
              <component
                v-if="user"
                :is="Element"
                :user="user"
              />
            </TabPanel>
          </TabPanels>
        </TabGroup>
      </div>
    </div>
  </div>
</template>
